import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {ButtonDirective} from 'primeng/button';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {TableColumn} from '@components/data-table/data-table.component';
import {FieldService, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {
  ModalHistoryVersion
} from '@app/pages/electronic-field-map/pages/fields/passport/forms/modal-history-version/modal-history-version.component';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {CommonModule, DatePipe} from '@angular/common';
import {UserService} from '@6grain/masofa-monolith-api-client/identity';
import {userNames} from '@app/utils/_msf';
import {firstValueFrom, from, of} from 'rxjs';
import {IMsfFormBuilder} from '@components/form-builder/form-builder';
import {
  ListTableWithoutFiltersWrapper
} from '@app/wrappers/pages/list-table-without-filters-wrapper/list-table-without-filters-wrapper';
import {RequestParams} from '@components/select/resource-component';
import {SortType} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/sortType';
import {catchError, finalize, map, mergeMap} from 'rxjs/operators';

interface FieldHistoryItem {
  id: string;
  createAt: Date | string;
  lastUpdateAt: Date | string;
  createUser: string | Guid;
  lastUpdateUser: string | Guid;
  createUserName?: string;
  lastUpdateUserName?: string;
  ownerId: string;
  newModel?: any;
  oldModel?: any;
  newModelJson?: string;
  oldModelJson?: string;
  changedFields?: ChangedField[];
  changedFieldsNames?: string[];
  versionUser?: string;
  versionUserName?: string;
}

interface ChangedField {
  fieldName: string;
  oldValue: any;
  newValue: any;
}

type Guid = string;

@Component({
  selector: 'app-passport-menu-general-history',
  templateUrl: './passport-menu-general-history.component.html',
  imports: [
    ButtonDirective,
    TranslatePipe,
    CommonModule,
    DateFormatLangPipe,
    ListTableWithoutFiltersWrapper
  ],
  providers: [DateFormatLangPipe, DatePipe],
  styleUrl: './passport-menu-general-history.component.scss',
  standalone: true
})
export class PassportMenuGeneralHistoryComponent
  implements OnInit, OnChanges {
  @ViewChild('changesTemplate', { static: true }) changesTemplate!: TemplateRef<any>;
  @ViewChild('userTemplate', { static: true }) userTemplate!: TemplateRef<any>;
  @ViewChild('actionOpenTemplate', { static: true }) actionOpenTemplate!: TemplateRef<any>;
  @ViewChild('dateTemplate', { static: true }) dateTemplate!: TemplateRef<any>;
  ref: DynamicDialogRef | undefined;

  @Input() fieldId?: string | null | undefined;
  @Input() seasonId?: string | null | undefined;
  @Input() formConfig: IMsfFormBuilder[] = [];
  @Input() subtitle: string = 'Field history';
  @Input() versionTitle: string = 'Field version';
  @Input() showRestoreButton: boolean = true;
  @Output() historyRestored = new EventEmitter<void>();

  protected readonly translate: TranslateService = inject(TranslateService);
  public dateFormatLangPipe: DateFormatLangPipe = inject(DateFormatLangPipe);

  fieldHistory: FieldHistoryItem[] = [];
  tableColumns: TableColumn[] = [];
  totalRecords: number = 0;
  loading: boolean = false;

  storageKey: string = 'field_history_table';
  filtersForms: IMsfFormBuilder[] = [];
  filtersParams: Object[] = [];
  toggleFilter: boolean | undefined = undefined;
  defaultParams: RequestParams = {
    take: 10,
    offset: 0,
    sortBy: 'createAt',
    sort: SortType.NUMBER_1,
    filters: []
  };

  getAllMethod: string = 'loadFieldHistory';
  getTotalMethod: string = 'loadFieldHistoryTotal';
  methodWithParams: boolean = true;
  historyService = {
    loadFieldHistory: (params?: RequestParams) => this.fetchFieldHistory(params),
    loadFieldHistoryTotal: (params?: RequestParams) => this.fetchFieldHistoryTotal(params)
  };
  service: any = this.historyService;

  // Map для хранения пользователей по ID
  usersMap: Map<string, any> = new Map();

  constructor(
    private cdr: ChangeDetectorRef,
    public dialogService: DialogService,
    private fieldService: FieldService,
    private seasonService: SeasonService,
    private userService: UserService,
  ) {
  }

  ngOnInit(): void {
    this.updateStorageKey();
    this.tableColumns = [
      {
        field: 'createAt',
        header: 'Date',
        type: 'custom',
        width: '200px',
        customTemplate: this.dateTemplate,
        defaultSorted: true
      },
      {
        field: 'versionUserName',
        header: 'User',
        sortable: false,
        type: 'custom',
        width: '200px',
        customTemplate: this.userTemplate
      },
      {
        field: 'changedFieldsNames',
        header: 'Changed fields',
        type: 'custom',
        sortable: false,
        width: 'auto',
        customTemplate: this.changesTemplate
      },
      {
        field: '',
        header: 'Actions',
        type: 'custom',
        sortable: false,
        width: '100px',
        customTemplate: this.actionOpenTemplate
      }
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fieldId'] || changes['seasonId']) {
      this.updateStorageKey();
      const fieldChanged = changes['fieldId'] && !changes['fieldId'].firstChange;
      const seasonChanged = changes['seasonId'] && !changes['seasonId'].firstChange;
      if (fieldChanged || seasonChanged) {
        this.refreshTable();
      }
    }

  }

  public reloadHistory(): void {
    this.refreshTable();
  }

  private fetchFieldHistory(params: RequestParams = this.defaultParams) {
    if (!this.ownerId) {
      this.totalRecords = 0;
      this.fieldHistory = [];
      return of([]);
    }

    return this.getHistoryRequest()
      .pipe(
        catchError((err) => {
          console.error('[History] Error loading history:', err);
          return of([]);
        }),
        mergeMap((history: any[]) => from(this.transformHistory(history || []))),
        map((historyItems: FieldHistoryItem[]) => {
          this.fieldHistory = historyItems;
          this.totalRecords = historyItems.length;
          return this.paginateHistory(historyItems, params);
        }),
        finalize(() => this.cdr.markForCheck())
      );
  }

  private fetchFieldHistoryTotal(params: RequestParams = this.defaultParams) {
    if (!this.ownerId) {
      this.totalRecords = 0;
      return of(0);
    }

    return this.getHistoryRequest().pipe(
      map((history: any[]) => {
        const filteredHistory = (history || []).filter((item: any) => item.ownerId === this.ownerId);
        this.totalRecords = filteredHistory.length;
        return this.totalRecords;
      }),
      catchError((err) => {
        console.error('[History] Error loading history total:', err);
        this.totalRecords = 0;
        return of(0);
      }),
      finalize(() => this.cdr.markForCheck())
    );
  }

  private async transformHistory(history: any[]): Promise<FieldHistoryItem[]> {
    const filteredHistory = (history || []).filter((item: any) => item.ownerId === this.ownerId);

    const userIds = new Set<string>();
    filteredHistory.forEach((item: any) => {
      if (item.newModel?.lastUpdateUser && typeof item.newModel.lastUpdateUser === 'string' && item.newModel.lastUpdateUser !== '00000000-0000-0000-0000-000000000000') {
        userIds.add(item.newModel.lastUpdateUser);
      }
      if (item.newModel?.createUser && typeof item.newModel.createUser === 'string' && item.newModel.createUser !== '00000000-0000-0000-0000-000000000000') {
        userIds.add(item.newModel.createUser);
      }
      if (item.oldModel?.lastUpdateUser && typeof item.oldModel.lastUpdateUser === 'string' && item.oldModel.lastUpdateUser !== '00000000-0000-0000-0000-000000000000') {
        userIds.add(item.oldModel.lastUpdateUser);
      }
      if (item.oldModel?.createUser && typeof item.oldModel.createUser === 'string' && item.oldModel.createUser !== '00000000-0000-0000-0000-000000000000') {
        userIds.add(item.oldModel.createUser);
      }
    });

    const userLoadPromises = Array.from(userIds).map(async (userId) => {
      if (this.usersMap.has(userId)) {
        return;
      }
      try {
        const user = await firstValueFrom(
          this.userService.identityUserGetByIdIdGet(userId).pipe(
            catchError((err) => {
              console.warn(`[History] Failed to load user ${userId}:`, err);
              return of(null);
            })
          )
        );
        if (user) {
          this.usersMap.set(userId, user);
        } else {
          console.warn(`[History] User ${userId} not found`);
        }
      } catch (error) {
        console.warn('[History] Failed to load user:', userId, error);
      }
    });

    await Promise.all(userLoadPromises);

    return filteredHistory.map((item: any, index: number) => {
      let newModel: any = item.newModel;
      let oldModel: any = item.oldModel;

      if (typeof item.newModelJson === 'string') {
        try {
          newModel = JSON.parse(item.newModelJson);
        } catch (e) {
          console.warn(`[History] Failed to parse newModelJson for item ${index}:`, e);
        }
      } else if (item.newModelJson) {
        newModel = item.newModelJson;
      }

      if (typeof item.oldModelJson === 'string') {
        try {
          oldModel = JSON.parse(item.oldModelJson);
        } catch (e) {
          console.warn(`[History] Failed to parse oldModelJson for item ${index}:`, e);
        }
      } else if (item.oldModelJson) {
        oldModel = item.oldModelJson;
      }

      const changedFields = this.getChangedFields(oldModel, newModel);
      const changedFieldsNames = changedFields.map(cf => this.getFieldDisplayName(cf.fieldName));

      const versionUserId = newModel?.lastUpdateUser || newModel?.createUser;
      const versionUser = versionUserId && versionUserId !== '00000000-0000-0000-0000-000000000000'
        ? this.usersMap.get(versionUserId)
        : null;
      const versionUserName = versionUser ? userNames(versionUser) : '-';

      return {
        id: item.id,
        createAt: item.createAt ? new Date(item.createAt) : null,
        lastUpdateAt: item.lastUpdateAt ? new Date(item.lastUpdateAt) : null,
        createUser: item.createUser || '',
        lastUpdateUser: item.lastUpdateUser || '',
        ownerId: item.ownerId,
        versionUser: versionUserId,
        versionUserName: versionUserName,
        changedFieldsNames: changedFieldsNames,
        newModel: newModel,
        oldModel: oldModel,
        changedFields: changedFields
      } as FieldHistoryItem;
    });
  }

  private paginateHistory(historyItems: FieldHistoryItem[], params?: RequestParams): FieldHistoryItem[] {
    const sortField = params?.sortBy || 'createAt';
    const sortDir = params?.sort === SortType.NUMBER_0 ? 1 : -1;

    const sorted = [...historyItems].sort((a, b) => {
      const valueA = (a as any)[sortField];
      const valueB = (b as any)[sortField];

      const resultA: number = valueA instanceof Date ? valueA.getTime() : new Date(valueA || 0).getTime();
      const resultB: number = valueB instanceof Date ? valueB.getTime() : new Date(valueB || 0).getTime();

      return (resultA - resultB) * sortDir;
    });

    const take = params?.take ?? this.defaultParams.take ?? sorted.length;
    const offset = params?.offset ?? 0;

    return sorted.slice(offset, offset + (take ?? sorted.length));
  }

  private updateStorageKey(): void {
    const idSuffix = this.ownerId ? `_${this.ownerId}` : '';
    this.storageKey = `${this.entityType}_history_table${idSuffix}`;
  }

  private refreshTable(): void {
    this.toggleFilter = !this.toggleFilter;
    this.cdr.detectChanges();
  }

  getChangedFields(oldModel: any, newModel: any): ChangedField[] {
    if (!oldModel && !newModel) return [];
    if (!oldModel) return []; // Created - no old values
    if (!newModel) return []; // Deleted - no new values

    const changes: ChangedField[] = [];
    const allKeys = new Set([...Object.keys(oldModel || {}), ...Object.keys(newModel || {})]);

    allKeys.forEach(key => {
      // Skip system fields, OwnerId, and metadata fields
      const systemFields = ['id', 'createAt', 'lastUpdateAt', 'createUser', 'lastUpdateUser', 'isPublic', 'status', 'ownerId'];
      if (systemFields.includes(key)) {
        return;
      }

      const oldValue = oldModel?.[key];
      const newValue = newModel?.[key];

      // Compare values more intelligently
      const oldVal = oldValue === null || oldValue === undefined ? '' : String(oldValue);
      const newVal = newValue === null || newValue === undefined ? '' : String(newValue);

      if (oldVal !== newVal) {
        changes.push({
          fieldName: key,
          oldValue: oldValue,
          newValue: newValue
        });
      }
    });

    return changes;
  }

  getFieldDisplayName(fieldName: string): string {
    const formConfigField = this.formConfig.find((field) => field.name === fieldName);
    if (formConfigField?.title) {
      return formConfigField.title;
    }

    // Используем ключи переводов, которые будут добавлены в файлы локализации
    const translationKeyMap: { [key: string]: string } = {
      'name': 'Field Name',
      'title': 'Title',
      'cropId': 'Culture',
      'plannedCropId': 'Planned culture',
      'varietyId': 'Variety',
      'plannedVarietyId': 'Planned variety',
      'seedVarietyId': 'Seed generation',
      'plantingMethodId': 'Planting Method',
      'plantingDate': 'Actual sowing date',
      'plantingDatePlan': 'Sowing date planned',
      'harvestingDate': 'Actual collection date',
      'harvestingDatePlan': 'Scheduled collection date',
      'fieldArea': 'Area',
      'regionId': 'Region',
      'soilTypeId': 'Soil type',
      'farmerId': 'Farmer',
      'status': 'Status',
      'lastUpdateAt': 'Last Update',
      'createAt': 'Create Date',
      'agroclimaticZoneId': 'Agroclimatic zone',
      'fieldTypeId': 'Field Type',
      'classifier': 'Classifier',
      'comment': 'Comment',
      'bonitetScore': 'Bonitet score',
      'altitudeAboveSeaLevel': 'Altitude above sea level',
      'cadastralNumber': 'Cadastre',
      'agricultureProducerId': 'Agriculture Producer',
      'irrigationTypeId': 'Irrigation Type',
      'irrigationSourceId': 'Irrigation Source',
      'waterSaving': 'Water Saving',
      'soilIndex': 'Soil Index',
      'control': 'Control'
    };

    // Возвращаем ключ для перевода, который будет переведен в шаблоне через pipe
    return translationKeyMap[fieldName] || fieldName;
  }

  // Получить основные поля Field для отображения версии
  getFieldMainFields(model: any): Array<{fieldName: string, value: any}> {
    if (!model || typeof model !== 'object') return [];

    // Основные поля Field для отображения
    const mainFields = [
      'name',
      'fieldArea',
      'regionId',
      'fieldTypeId',
      'cadastralNumber',
      'soilTypeId',
      'agroclimaticZoneId',
      'comment',
      'agricultureProducerId',
      'irrigationTypeId',
      'irrigationSourceId',
      'waterSaving',
      'soilIndex',
      'bonitetScore',
      'classifier',
      'altitudeAboveSeaLevel',
      'control'
    ];

    return mainFields
      .filter(fieldName => model.hasOwnProperty(fieldName) && (model[fieldName] !== null && model[fieldName] !== undefined && model[fieldName] !== ''))
      .map(fieldName => ({
        fieldName,
        value: model[fieldName]
      }));
  }

  formatFieldValue(value: any, fieldName: string): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    // Format based on field type
    if (fieldName === 'fieldArea' || fieldName === 'altitudeAboveSeaLevel') {
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    }

    if (fieldName === 'status') {
      return value === 1 ? this.translate.instant('Active') : this.translate.instant('Inactive');
    }

    // Handle GUID fields - try to resolve names
    if (fieldName.endsWith('Id') && typeof value === 'string' && value.length === 36) {
      // For other GUIDs, show shortened version
      return value.substring(0, 8) + '...';
    }

    // Handle boolean
    if (typeof value === 'boolean') {
      return value ? this.translate.instant('Yes') : this.translate.instant('No');
    }

    // Handle dates
    if (fieldName.includes('Date') || fieldName.includes('At')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return this.dateFormatLangPipe.transform(date, this.translate.currentLang) || String(value);
        }
      } catch (e) {
        // Ignore date parsing errors
      }
    }

    return String(value);
  }

  // Открыть модалку для просмотра версии истории
  openHistoryVersionModal(historyItem: FieldHistoryItem): void {

    const versionModel = historyItem.oldModel || historyItem.newModel;
    if (!versionModel) {
      console.warn('[History] No version model available, cannot open modal');
      return;
    }

    // Создаем копию модели для отображения
    const displayModel = {...versionModel};
    displayModel.id = this.ownerId || displayModel.id;

    const dateStr = historyItem.createAt
      ? this.dateFormatLangPipe.transform(historyItem.createAt, this.translate.currentLang)
      : '';


    this.ref = this.dialogService.open(ModalHistoryVersion, {
      header: this.translate.instant(this.versionTitle) + (dateStr ? ' - ' + dateStr : ''),
      width: '50%',
      closable: true,
      modal: true,
      data: {
        model: displayModel,
        entityType: this.entityType,
        fields: this.toReadOnlyFormFields(
          this.formConfig.length > 0 ? this.formConfig : this.getEditFormConfig(),
        ),
        isReadOnly: true,
        showRestoreButton: this.showRestoreButton,
      },
    });

    this.ref.onClose.subscribe((result) => {
      if (result?.restore) {

        // Перезагружаем историю после восстановления
        this.refreshTable();
        // Уведомляем родительский компонент о восстановлении
        this.historyRestored.emit();
      }
    });
  }

  getEditFormConfig(): IMsfFormBuilder[] {
    // Fallback if formConfig is not provided
    return this.formConfig || [];
  }

  private toReadOnlyFormFields(fields: IMsfFormBuilder[]): IMsfFormBuilder[] {
    return fields.map((field) => ({...field, disabled: () => true}));
  }

  onRowSelect($event: any) {
  }

  onRowUnselect($event: any) {
  }

  onPageChange($event: any) {
  }

  onSort($event: any) {
  }

  onFilter($event: any) {
  }

  onSelectAllChange($event: boolean) {
  }

  private get ownerId(): string | null {
    return this.seasonId ?? this.fieldId ?? null;
  }

  private get entityType(): 'field' | 'season' {
    return this.seasonId ? 'season' : 'field';
  }

  private getHistoryRequest() {
    if (this.seasonId) {
      return this.seasonService.cropMonitoringSeasonGetHistoryGet(this.seasonId);
    }

    if (this.fieldId) {
      return this.fieldService.cropMonitoringFieldGetHistoryGet(this.fieldId);
    }

    return of([]);
  }
}
